// CartProductRecommendations: Custom element for displaying product recommendations in the mini cart
class CartProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.products = [];
    // Bind event handlers to the instance
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleArrow = this.handleArrow.bind(this);
    this.slider = null;
    this.position = 0;
    this.boundWheelHandler = null;
  }

  connectedCallback() {
    // Called when the element is added to the DOM
    this.render();
    this.boundCartUpdate = this.handleCartUpdate.bind(this);
    document.addEventListener('cart:update', this.boundCartUpdate);
  }

  disconnectedCallback() {
    // Called when the element is removed from the DOM
    document.removeEventListener('cart:update', this.boundCartUpdate);
    this.removeSliderListeners();
  }

  handleCartUpdate() {
    // Re-render and reattach listeners when the cart is updated
    this.render();
  }

  removeSliderListeners() {
    // Remove the wheel event listener if it exists
    if (this.slider && this.boundWheelHandler) {
      this.slider.removeEventListener('wheel', this.boundWheelHandler);
      this.boundWheelHandler = null;
    }
    this.removeMouseDragListeners();
    // Defensive: try to remove again in case of duplicate
    if (this.slider && this.boundWheelHandler) {
      this.slider.removeEventListener('wheel', this.boundWheelHandler);
      this.boundWheelHandler = null;
    }
  }

  async render() {
    // Main render method for the recommendations component
    let title = this.getAttribute('data-title') || '';
    let emptyTitle = this.getAttribute('data-empty-title') || '';
    let productCount = Math.max(2, parseInt(this.getAttribute('data-product-count') || '4', 10));
    let productsAttr = this.getAttribute('data-products') || '';
    let sectionId = this.getAttribute('data-section-id');
    let recoProductId = this.getAttribute('data-recommendation-product-id');
    let cart = window?.Shopify?.cart || {};
    let products = [];

    // --- OPTIMIZATION: Read manual products from injected JSON ---
    async function getManualProductsData() {
      const script = document.getElementById('manual-products-data');
      if (!script) return [];
      try {
        return await JSON.parse(script.textContent);
      } catch (e) {
        //console.error('Error parsing manual products data:', e);
        return [];
      }
    }
    if (productsAttr == "true") {
      // If the script with manual data exists, use it
      const manualProducts = await getManualProductsData();
      console.log(manualProducts)
      if (manualProducts.length > 0) {
        console.log('Using manual products from JSON data.');
        products = manualProducts.slice(0, productCount);
      } else {
        // Fallback: previous method (fetch by handle)
        let handles = productsAttr.split(',').map(p => p.trim()).filter(Boolean);
        products = await Promise.all(handles.map(handle => this.fetchProduct(handle)));
        products = products.filter(Boolean).slice(0, productCount);
      }
    } else if (recoProductId) {
      // Use the product_id passed by data attribute
      products = await this.fetchRecommendations(recoProductId, productCount);
    } else {
      // Fallback: use the first product in the cart if it exists
      let firstCartItem = cart.items && cart.items.length ? cart.items[0] : null;
      if (firstCartItem) {
        products = await this.fetchRecommendations(firstCartItem.product_id, productCount);
      }
    }
    this.products = products;
    // Show the correct title: if there are recommended products, use the normal title
    let showTitle = (products && products.length > 0) ? title : emptyTitle;
    this.innerHTML = `
      <div class="minicart__product-recommendations__header">
        <span class="minicart__product-recommendations__title">${showTitle}</span>
        <div class="minicart__product-recommendations__arrows">
          <button type="button" class="minicart__product-recommendations__arrow left" aria-label="Previous"><span class="icon icon-left"></span></button>
          <button type="button" class="minicart__product-recommendations__arrow right" aria-label="Next"><span class="icon icon-right"></span></button>
        </div>
      </div>
      <div class="minicart__product-recommendations__slider-wrapper">
        <div class="minicart__product-recommendations__slider">
          ${products.map(product => this.renderProduct(product)).join('')}
        </div>
      </div>
    `;
    // Always get the slider and arrows from the current DOM
    this.slider = this.querySelector('.minicart__product-recommendations__slider');
    let leftArrow = this.querySelector('.minicart__product-recommendations__arrow.left');
    let rightArrow = this.querySelector('.minicart__product-recommendations__arrow.right');

    // Disable arrows if there is no overflow
    setTimeout(() => {
      let sliderWrapper = this.querySelector('.minicart__product-recommendations__slider-wrapper');
      if (!this.slider || !sliderWrapper) return;
      if (this.slider.scrollWidth <= sliderWrapper.offsetWidth + 1) {
        leftArrow.disabled = true;
        rightArrow.disabled = true;
      } else {
        leftArrow.disabled = false;
        rightArrow.disabled = false;
      }
    }, 100);

    this.removeSliderListeners();

    // Reattach listeners
    if (leftArrow) leftArrow.addEventListener('click', () => this.handleArrow(-1));
    if (rightArrow) rightArrow.addEventListener('click', () => this.handleArrow(1));
    this.querySelectorAll('.minicart__product-recommendations__add').forEach(btn => {
      btn.addEventListener('click', this.handleAddToCart);
    });

    // Only allow scroll if there is overflow

    // Mouse drag for slider
    this.addMouseDragListeners();

    // Add wheel event for horizontal scroll if needed
    setTimeout(() => {
      let sliderWrapper = this.querySelector('.minicart__product-recommendations__slider-wrapper');
      if (this.slider && sliderWrapper && this.slider.scrollWidth > sliderWrapper.offsetWidth + 1) {
        // Save reference to remove the listener later
        this.boundWheelHandler = (e) => {
        e.preventDefault();
        const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
        sliderWrapper.scrollLeft += delta;
      };
        this.slider.addEventListener('wheel', this.boundWheelHandler);
      }
    }, 100);
  }

  updateTitles() {
    // Update the title based on cart state
    let cart = window?.Shopify?.cart || {};
    let title = this.getAttribute('data-title') || '';
    let emptyTitle = this.getAttribute('data-empty-title') || '';
    let titleEl = this.querySelector('.minicart__product-recommendations__title');
    if (titleEl) {
      titleEl.textContent = cart.item_count > 0 ? title : emptyTitle;
    }
  }

  async fetchProduct(handle) {
    // Fetch product data by handle from Shopify
    try {
      let res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  async fetchRecommendations(productId, limit) {
    // Fetch product recommendations from Shopify
    try {
      let res = await fetch(`/recommendations/products.json?product_id=${productId}&limit=${limit}`);
      if (!res.ok) return [];
      let data = await res.json();
      return data.products || [];
    } catch {
      return [];
    }
  }

  renderProduct(product) {
    // Render a single product card for the slider
    if (!product) return '';
    let image = product.featured_image || product.images?.[0] || '';
    let title = product.title || '';
    let type = product.type || '';
    let price = product.variants?.[0]?.price || product.price || 0;
    let priceFormatted = window?.Shopify?.formatMoney ? window.Shopify.formatMoney(price) : `$${(price/100).toFixed(0)}`;
    let variantId = product.variants?.[0]?.id || (product.variants && product.variants.length ? product.variants[0].id : '');
    return `
      <div class="minicart__product-recommendations__item">
        <img src="${image}" alt="${title}" class="minicart__product-recommendations__image" loading="lazy" />
        <div class="minicart__product-recommendations__info">
          <div class="minicart__product-recommendations__product-title">${title}</div>
          <div class="minicart__product-recommendations__product-type">${type}</div>
          <button type="button" class="minicart__product-recommendations__add" data-variant-id="${variantId}" data-price="${priceFormatted}">ADD TO BAG • ${priceFormatted}</button>
        </div>
      </div>
    `;
  }

  async handleAddToCart(e) {
    // Handle add to cart button click for a recommended product
    let btn = e.currentTarget;
    let variantId = btn.getAttribute('data-variant-id');
    if (!variantId || btn.disabled) return;
    btn.disabled = true;
    try {
      let res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 })
      });
      if (res.ok) {
        // Force mini cart reload to reflect the new product
        document.dispatchEvent(new CustomEvent('minicart:open'));
      } else {
        btn.textContent = 'Error';
      }
    } catch {
      btn.textContent = 'Error';
    }
    setTimeout(() => {
      btn.disabled = false;
    }, 1500);
  }

  handleArrow(dir) {
    // Handle left/right arrow navigation for the slider
    if (!this.slider) return;
    let item = this.slider.querySelector('.minicart__product-recommendations__item');
    if (!item) return;
    let style = window.getComputedStyle(item);
    let gap = parseInt(style.marginRight) || 16;
    let scrollAmount = item.offsetWidth + gap;
    console.log('Scrolling by', dir * scrollAmount);
    this.slider.parentElement.scrollBy({
      left: dir * scrollAmount,
      behavior: 'smooth'
    });
  }

  // --- Drag with mouse ---
  addMouseDragListeners() {
    // Add mouse drag listeners for horizontal slider scroll
    const sliderWrapper = this.slider?.parentElement;
    if (!sliderWrapper) return;

    let isDown = false;
    let startX;
    let scrollLeftStart;

    const start = (e) => {
      // Only mouse, not touch
      if (e.type.includes('touch')) return;
      isDown = true;
      startX = e.pageX - sliderWrapper.offsetLeft;
      scrollLeftStart = sliderWrapper.scrollLeft;
      sliderWrapper.classList.add("dragging");
    };

    const move = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - sliderWrapper.offsetLeft;
      const walk = (x - startX) * 1;
      sliderWrapper.scrollLeft = scrollLeftStart - walk;
    };

    const end = () => {
      isDown = false;
      sliderWrapper.classList.remove("dragging");
    };

    this.mouseDragHandlers = { start, move, end };

    sliderWrapper.addEventListener("mousedown", start);
    sliderWrapper.addEventListener("mousemove", move);
    sliderWrapper.addEventListener("mouseleave", end);
    sliderWrapper.addEventListener("mouseup", end);
  }

  removeMouseDragListeners() {
    // Remove mouse drag listeners for the slider
    const sliderWrapper = this.slider?.parentElement;
    if (!sliderWrapper || !this.mouseDragHandlers) return;

    const { start, move, end } = this.mouseDragHandlers;

    sliderWrapper.removeEventListener("mousedown", start);
    sliderWrapper.removeEventListener("mousemove", move);
    sliderWrapper.removeEventListener("mouseleave", end);
    sliderWrapper.removeEventListener("mouseup", end);

    this.mouseDragHandlers = null;
  }

}

customElements.define('cart-product-recommendations', CartProductRecommendations);